// src/components/Settings/DataAndBackups.jsx
// Backup + manual Send/Receive with conflicts & pills

import React, { useRef, useState } from "react";
import { useStorage } from "@/contexts/StorageContext";

export default function DataAndBackups() {
  const {
    mode, hasFolder, baseDirName, workspaceName,
    isDriveLinked, isDriveAuthed, busy,
    lastSynced, lastConflict,
    pickFolder, clearFolderSelection,
    connectDrive, disconnectDrive,
    saveAll, reload, forceSend,
    exportAll, importBackup, healthCheck,
    hasWorkspace, activeWorkspace,
  } = useStorage();

  const fileRef = useRef(null);
  const [message, setMessage] = useState("");
  const [localBusy, setLocalBusy] = useState(false);

  const setMsg = (m) => setMessage(m || "");

  const onImportClick = () => fileRef.current?.click();
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLocalBusy(true);
    try {
      const res = await importBackup(f);
      setMsg(`Imported ${res.keys} keys from backup`);
    } catch (err) {
      setMsg(`Import failed: ${err?.message || String(err)}`);
    } finally {
      e.target.value = "";
      setLocalBusy(false);
    }
  };

  const onExport = async () => {
    setLocalBusy(true);
    try {
      const res = await exportAll();
      setMsg(`Backup exported to ${res.where}${res.name ? ` (${res.name})` : ""}`);
    } catch (e) {
      setMsg(`Export failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  const onSend = async () => {
    setLocalBusy(true);
    try {
      const res = await saveAll();
      if (res?.conflict) {
        setMsg(`Warning: Conflict: remote updated at ${res.remoteMeta?.updatedAt}. Click "Force Send" to overwrite.`);
        return;
      }
      if (res.where === "drive") setMsg(`Sent to Drive (${res.bytes} bytes)`);
      else if (res.where === "folder") setMsg(`Saved to Folder (${res.bytes} bytes)`);
      else setMsg("Nothing to save (no storage linked)");
    } catch (e) {
      setMsg(`Send failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  const onForceSend = async () => {
    setLocalBusy(true);
    try {
      const res = await forceSend();
  if (res.where === "drive") setMsg(`Force-sent to Drive (${res.bytes} bytes)`);
  else if (res.where === "folder") setMsg(`Force-saved to Folder (${res.bytes} bytes)`);
  else setMsg("Force send failed (no storage linked)");
    } catch (e) {
      setMsg(`Force send failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  const onReceive = async () => {
    setLocalBusy(true);
    try {
      const res = await reload();
  setMsg(res.ok ? `Received ${res.keys} items${res.meta?.updatedAt ? ` (updated ${res.meta.updatedAt})` : ""}` : `Receive failed: ${res.reason}`);
    } catch (e) {
      setMsg(`Receive failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  const onHealth = async () => {
    setLocalBusy(true);
    try {
      const res = await healthCheck();
      setMsg(`Health: ${res.ok ? "OK" : "NOT OK"} (${res.mode || res.reason})`);
    } catch (e) {
      setMsg(`Health check failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  const lastSyncedLabel = lastSynced?.updatedAt
    ? new Date(lastSynced.updatedAt).toLocaleString()
    : "never";

  const anyBusy = !!busy || localBusy;

  const onDisconnectDrive = async () => {
    const confirmed = confirm(
      "⚠️ Disconnect from Google Drive?\n\n" +
      "This will immediately clear all data from the app. " +
      "Your workspace data will remain in Google Drive and can be reconnected later."
    );
    if (!confirmed) return;
    setLocalBusy(true);
    try {
      await disconnectDrive();
      setMsg("Disconnected from Google Drive. All data cleared.");
    } catch (e) {
      setMsg(`Disconnect failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  const onForgetFolder = async () => {
    const confirmed = confirm(
      "⚠️ Forget local folder?\n\n" +
      "This will immediately clear all data from the app. " +
      "Your workspace data will remain in the folder and can be reconnected later."
    );
    if (!confirmed) return;
    setLocalBusy(true);
    try {
      await clearFolderSelection();
      setMsg("Forgot local folder. All data cleared.");
    } catch (e) {
      setMsg(`Forget failed: ${e?.message || String(e)}`);
    } finally {
      setLocalBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 space-y-8">
      <h1 className="text-2xl font-semibold text-white/90">Studio Data &amp; Backup</h1>
      
      {/* Workspace Status */}
      {!hasWorkspace && (
        <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-200">
          ⚠️ <strong>No workspace selected.</strong> Connect a workspace to load and save your studio data.
        </div>
      )}
      {hasWorkspace && activeWorkspace && (
        <div className="rounded-md bg-blue-500/10 border border-blue-500/30 px-3 py-2 text-sm text-blue-200">
          📁 <strong>Active Workspace:</strong> {activeWorkspace.type === "drive" ? "Google Drive" : "Local Folder"} 
          {activeWorkspace.name && ` (${activeWorkspace.name})`}
        </div>
      )}
      
      {message && (
        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm text-emerald-200">
          {message}
        </div>
      )}

      {/* Cloud Sync (Google Drive) */}
      <Card>
        <CardHeader
          title="Cloud Sync (Google Drive)"
          right={
            isDriveLinked ? (
              <StatusPill ok={isDriveAuthed}>
                {isDriveAuthed ? "Linked" : "Linked (reauth pending)"}
              </StatusPill>
            ) : (
              <StatusPill>Not linked</StatusPill>
            )
          }
        />
        <div className="flex flex-wrap items-center gap-3">
          {isDriveLinked ? (
            <>
              <span className="text-sm text-white/70">
                Workspace:&nbsp;<strong>{workspaceName || "Google Drive"}</strong>
                &nbsp;&nbsp;Folder:&nbsp;<code className="text-white/80">/Mudo</code>
                <span className="ml-2 text-xs opacity-60">Last synced: {lastSyncedLabel}</span>
              </span>
              <div className="flex gap-2">
                <Button onClick={onSend} disabled={anyBusy || !hasWorkspace}>{anyBusy ? "Working…" : "Send"}</Button>
                <Button onClick={onReceive} variant="ghost" disabled={anyBusy || !hasWorkspace}>Receive</Button>
                {lastConflict?.where === "drive" && (
                  <Button onClick={onForceSend} variant="danger" disabled={anyBusy || !hasWorkspace}>Force Send</Button>
                )}
                <Button onClick={onExport} variant="ghost" disabled={anyBusy}>Export</Button>
                <Button onClick={onHealth} variant="ghost" disabled={anyBusy}>Health</Button>
                <Button onClick={onDisconnectDrive} variant="danger" disabled={anyBusy}>Disconnect</Button>
              </div>
            </>
          ) : (
            <Button onClick={connectDrive} disabled={anyBusy}>
              {anyBusy ? "Connecting…" : "Connect Google Drive"}
            </Button>
          )}
        </div>
      </Card>

      {/* Local Folder */}
      <Card>
        <CardHeader
          title="Local Folder"
          right={hasFolder ? <StatusPill ok>Linked</StatusPill> : <StatusPill>Not linked</StatusPill>}
        />
        {hasFolder ? (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-white/70">
              Linked:&nbsp;<strong>{baseDirName}</strong>&nbsp;/&nbsp;<code>/Mudo</code>
              <span className="ml-2 text-xs opacity-60">Last synced: {lastSyncedLabel}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={pickFolder} disabled={anyBusy}>Change Folder</Button>
              <Button onClick={onForgetFolder} variant="danger" disabled={anyBusy}>Forget</Button>
              <Button onClick={onReceive} variant="ghost" disabled={anyBusy || !hasWorkspace}>Receive</Button>
              <Button onClick={onSend} disabled={anyBusy || !hasWorkspace}>Send</Button>
              {lastConflict?.where === "folder" && (
                <Button onClick={onForceSend} variant="danger" disabled={anyBusy || !hasWorkspace}>Force Send</Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button onClick={pickFolder} disabled={anyBusy}>Select Folder</Button>
            <span className="text-xs opacity-60">No folder selected.</span>
          </div>
        )}
      </Card>

      {/* Manual Backup */}
      <Card>
        <CardHeader title="Manual Backup (ZIP / JSON)" />
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onExport} disabled={anyBusy}>Export Backup</Button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,.zip,application/json,application/zip"
            className="hidden"
            onChange={onImportFile}
          />
          <Button onClick={onImportClick} variant="ghost" disabled={anyBusy}>Import Backup</Button>
        </div>
      </Card>
    </div>
  );
}

/* ---------- tiny UI primitives ---------- */
function Card({ children }) {
  return <div className="rounded-xl border border-white/10 bg-white/5 p-4">{children}</div>;
}
function CardHeader({ title, right }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <div className="text-lg font-semibold text-white/90">{title}</div>
      {right}
    </div>
  );
}
function StatusPill({ ok = false, children }) {
  const cls = ok
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
    : "bg-red-500/10 text-red-300 border-red-400/30";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${cls}`}>
      {children}
    </span>
  );
}
function Button({ children, onClick, disabled, variant = "primary" }) {
  const base =
    "px-3 py-1.5 rounded-md text-sm transition focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-blue-600 hover:bg-blue-500 text-white",
    ghost: "bg-white/10 hover:bg-white/15 text-white",
    danger: "bg-red-600 hover:bg-red-500 text-white",
  }[variant];
  return (
    <button className={`${base} ${styles}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}
